// Tiny auth context for the user-facing surface. State of record is
// the cookie set by /v1/auth/*; this hook remembers the most recent
// probe so the UI doesn't re-probe on every render, and caches the
// account summary from GET /v1/me.
//
// One probe per app load (mount of <AuthProvider>). login() posts the
// credentials then refreshes from /v1/me so the rest of the tree gets
// the full account record (email, tier, display name, auth methods).
// The plumbing (probe/refresh/logout/status) lives in the shared
// lib/useSession hook; this file composes login() on top.

import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import type { UnitSettings } from '@neomaris/protocol';

import { api } from './api';
import { useAccountScreenLook, type AccountLook } from './lib/accountScreenLook';
import { markDemoGuest, type DemoLease } from './lib/demo';
import { enableSessionRenewal } from './lib/sessionRenewal';
import { useSession } from './lib/useSession';

// Turn on session renewal for the whole user plane, at module scope so it is
// in force before the first api() call rather than one render behind it.
//
// A "stay signed in" session runs up to a year but its access token is capped
// at 24 hours (task remember_me, ADR 0190), so every authenticated call needs
// to be able to rotate underneath itself — not just the mount probe, which an
// already-open dashboard never runs again.
//
// The admin console shares this bundle, so the switch is not by itself the
// boundary — `isRenewablePath` is, refusing to renew for `/v1/admin/*` and
// `/v1/auth/*`. That keeps the two planes separate (ADR 0040) even though one
// import turns the machinery on for both.
enableSessionRenewal('/v1/auth/refresh');

// Mirrors the GET /v1/me response shape (src/api/me/routes.ts).
export interface User {
  id: string;
  email: string;
  displayName: string | null;
  tier: 'free' | 'pro' | 'enterprise';
  emailVerified: boolean;
  authMethods: { password: boolean; google: boolean };
  createdAt: string;
  // null until the account picks its own units. Read it through useUnits(),
  // which is the one place the null becomes DEFAULT_UNIT_SETTINGS.
  unitSettings: UnitSettings | null;
  // The last System, Light or Dark this account chose (TOM-944); null when
  // never chosen, absent from an older server. useAccountScreenLook applies it.
  screenLook?: AccountLook | null;
  // A Try it guest's lease (TOM-868, cloud ADR 0419); null for every other
  // account, and absent from an older server.
  demo?: DemoLease | null;
}

type Status = 'unknown' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: Status;
  isAuthenticated: boolean;
  user: User | null;
  refresh: () => Promise<Status>;
  login: (
    email: string,
    password: string,
    turnstileToken?: string | null,
    remember?: boolean
  ) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // GET /v1/me is the session probe: 200 + the account summary when the
  // cookie is valid, 401 otherwise. The response IS the User record.
  const session = useSession<User>({
    whoamiUrl: '/v1/me',
    logoutUrl: '/v1/auth/logout',
    parseRecord: (raw) => {
      const user = raw as User;
      // Remembered for this tab, so a 401 after the lease ends sends the
      // visitor to /try rather than to sign-in (TOM-868).
      markDemoGuest(user.demo != null);
      return user;
    },
  });

  // Brings the account's last look to this browser, and saves new choices.
  useAccountScreenLook(session.record?.id ?? null, session.record?.screenLook);

  const { refresh } = session;
  const login = useCallback(
    async (email: string, password: string, turnstileToken?: string | null, remember?: boolean) => {
      const body: {
        email: string;
        password: string;
        'cf-turnstile-response'?: string;
        remember?: boolean;
      } = {
        email,
        password,
      };
      // Sent only when login has stepped up to a challenge after a failure
      // (threat A1); the server requires it per-IP once an attempt has failed.
      if (turnstileToken) body['cf-turnstile-response'] = turnstileToken;
      // Only sent when the user actually ticked the box — the server rejects
      // anything but a real boolean, and absent means the 24-hour default.
      if (remember === true) body.remember = true;
      await api('/v1/auth/login', { method: 'POST', body });
      // Pull the full account record and flip status → authenticated. refresh()
      // never throws (it maps any failure to 'anonymous'), so surface a clear
      // error when the post-login probe didn't land — otherwise login()
      // resolves as success while the session is left signed out, and the
      // caller would navigate into a view that immediately bounces to /login.
      const status = await refresh();
      if (status !== 'authenticated') {
        throw Object.assign(new Error('post-login session probe failed'), {
          body: { message: 'Signed in, but loading your account failed. Please try again.' },
        });
      }
    },
    [refresh]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status: session.status,
      isAuthenticated: session.status === 'authenticated',
      user: session.record,
      refresh: session.refresh,
      login,
      logout: session.logout,
    }),
    [session.status, session.record, session.refresh, session.logout, login]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Whether the signed-in user is a Try it guest (TOM-868). Safe outside the
 * provider, where it is false, so components rendered alone in tests can call it.
 */
export function useDemoGuest(): boolean {
  return useContext(AuthContext)?.user?.demo != null;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
